
export class Store {

    constructor() {
        this.handlers = [];
        this.listeners = {};
        this.updatedAt = null;
        this.previousData = {};
        this.data = {};
    }

    generateObjectId() {
        return _.uniqueId('l');
    }

    /**
     * Subskrybuje konkretny event:
     *
     * Możliwie eventy:
     * change            - zmienił się dowolny stan
     * change:state      - zmienił się dany stan np: currentUser - `change:currentUser`
     * change:state:key  - zmieniła się konkretna warotść w stanie - `change:currentUser:name`
     *
     * @param {String|Array} key     - nazwa eventu
     * @param {Function}     handler - funkcja obsługująca event
     */
    on(key, handler) {
        if (_.isArray(key)) {
            for (let e of key) {
                this.on(e, handler);
            }
        } else {
            this.handlers.push({
                id: this.generateObjectId(),
                key,
                handler
            });
        }
    }

    /**
     * Usuwa subskrybcję
     * @param {String} key       - nazwa eventu
     * @param {Function} handlerFn - funkcja obsługująca event
     */
    off(key, handlerFn) {
        if (_.isArray(key)) {
            for (let e of key) {
                this.off(e, handlerFn);
            }
        } else {
            const nextHandlers = [];
            for (let handler of this.handlers) {
                if (
                    (handler.key !== key && !handlerFn) ||
                    (handler.key !== key && handlerFn && handler.handler !== handlerFn)
                ) {
                    nextHandlers.push(handler);
                }
            }
            this.handlers = nextHandlers;
        }
    }


    /**
     * Dodaje obiekt, kóry będzie informowany o zmianach
     * @param {Object} object
     * @param {Object} handlers
     * @returns {Store}
     */
    addEventListener(object, handlers) {
        if (!object._eventListenerId) {
            object._eventListenerId = this.generateObjectId();
        }

        let listener = this.listeners[object._eventListenerId];
        if (!listener) {
            listener = this.listeners[object._eventListenerId] = {
                id: object._eventListenerId,
                handlers: [],
            };
        }

        for (let key in handlers) {
            if (handlers.hasOwnProperty(key)) {
                listener.handlers.push({
                    key,
                    id: this.generateObjectId(),
                    handler: handlers[key],
                });
            }
        }
        return this;
    }


    /**
     * Usuwa informaowanie obiektu o zmianach
     * @param {Object} object
     * @returns {Store}
     */
    removeEventListener(object) {
        if (!object._eventListenerId) {
            return this;
        }
        if (this.listeners[object._eventListenerId]) {
            delete this.listeners[object._eventListenerId];
        }
        return this;
    }


    /**
     * Subskrybuje wszystkie zmiany
     * @param {Function} handler - funkcja obsługująca event
     */
    subscribe(handler) {
        return this.on('change', handler);
    }

    /**
     * Usuwa subskrybcję wszystkich zmian dla konkretnego handlera
     * @param {Function} handler - funkcja obsługująca event
     */
    unsubscribe(handler) {
        return this.off('change', handler);
    }

    /**
     * Powiadamia subskrybentów o zmianie stanu
     * @param {String} key
     * @param {*}      data
     * @param {*}      previousData
     * @param {Object} options
     * @internal
     */
    trigger(key, data, previousData, options) {
        let handler;
        for (handler of this.handlers) {
            if (handler.key === key) {
                handler.handler(data, previousData, options);
            }
        }

        for (let listenerId in this.listeners) {
            if (this.listeners.hasOwnProperty(listenerId)) {
                for (handler of this.listeners[listenerId].handlers) {
                    if (handler.key === key || handler.key === '*') {
                        handler.handler(data, previousData, options);
                    }
                }
            }
        }
    }

    /**
     * Ustawia stan.
     * Po ustawieniu stanu powiadamia subskrybentów o zmianie @see on()
     *
     * możliwe parametry w `options`:
     *  - {Boolean }merge - łączy nowy stan z poprzednim jeśli `true` (dom. `false`)
     *
     * @param {String} key      Nazwa stanu
     * @param {*}      data     Nowa reprezentacja stanu
     * @param {Object} options  Dodatkowe prametry
     */
    set(key, data, options = {merge: false}) {

        if (options.merge === true) {
            if (_.isArray(data)) {
                data = this.mergeStates(this.get(key), data, options);
            } else {
                data = Object.assign({}, this.get(key) || {}, data);
            }
        }

        this.previousData = Object.assign({}, this.data);
        this.data[key] = data;
        this.updatedAt = Date.now();

        // Powiadamia o change:state:key
        this.triggerChangedKeysEvents(this.data, this.previousData, options, 'change');
        this.trigger('change', this.data, this.previousData, options);
    }

    /**
     * Powiadamia o change:state:key
     * @param data
     * @param previousData
     * @param options
     * @param path
     */
    triggerChangedKeysEvents(data, previousData, options, path = 'change') {
        previousData = previousData || {};
        if (_.isObject(data) && !_.isArray(data)) {
            for (let field of Object.keys(data)) {
                if (data[field] !== previousData[field]) {
                    this.trigger(`${path}:${field}`, data[field], previousData[field], options);
                    this.triggerChangedKeysEvents(data[field], previousData[field], options, `${path}:${field}`);
                }
            }
        }
    }

    /**
     * Łączy nowy stan ze starym @see set()
     * @param {String }      key
     * @param {Object|Array} data
     * @param {Object}       options
     */
    merge(key, data, options = {}) {
        this.set(key, data, Object.assign({}, options, { merge: true }));
    }

    /**
     * Zwraca stan
     * @param {String} key
     * @param {*}      defaultValue
     * @returns {*}
     */
    get(key, defaultValue = null) {
        return key ? (this.data[key] || defaultValue) : this.data;
    }

    /**
     * Łączy dwa stany, które są tablicami.
     * Zwraca nową tablicę
     *
     * możliwe parametry w `options`:
     *  - {Boolean} remove     - Usuwa obiekty niewystępujące w nowym stanie (dom. `false`)
     *  - {String}  primaryKey - klucz podstawowy / id obiektów w tablicy (dom. `id`)
     *
     * @param {Array}  state    Stary stan do złączenia
     * @param {Array}  data     Nowy stan do złącznia
     * @param {Object} options  Dodatkowe parametry
     * @returns {Array}
     * @internal
     */
    mergeStates(state, data, options = {}) {
        const pk = options.primaryKey || 'id';
        const remove = Boolean(options.remove);

        // Łączy stany
        let nextState = [...(state || [])];
        let model;
        for (let row of data) {

            if (_.isFunction(pk)) {
                model = _.find(state, m => pk(m, row));
            } else {
                model = _.find(state, { [pk]: row[pk] });
            }

            if (model) {
                nextState[state.indexOf(model)] = Object.assign({}, model, row);
            } else {
                if (options.prepend === true) {
                    nextState.unshift(row);
                } else {
                    nextState.push(row);
                }
            }
        }

        // Usuwa obiekty niewystępujące w nowym stanie
        if (remove) {
            const dataPks = data.map(row => row[pk]);
            nextState = nextState.filter(row => _.includes(dataPks, row[pk]));
        }

        return nextState;
    }
}

// Default Store
export const store = new Store();
